import { useEffect, useRef, useState } from 'react';
import { loadFacebookSdk } from './facebook-sdk';

const FB_MESSAGE_ORIGINS = new Set(['https://www.facebook.com', 'https://web.facebook.com']);

export interface EmbeddedSignupResult {
  code: string;
  wabaId: string;
  phoneNumberId: string;
}

/**
 * Shared Meta Embedded Signup popup plumbing: launches `FB.login`, captures
 * `waba_id`/`phone_number_id` from Meta's own `WA_EMBEDDED_SIGNUP`
 * postMessage events, and combines them with `FB.login`'s own `code`
 * callback. Used both for first-time connect and for re-authorizing an
 * already-connected channel's token - the popup flow itself is identical
 * either way, only what the caller does with the result differs.
 */
export function useEmbeddedSignupPopup(onCancelOrError: (message: string) => void) {
  const [launching, setLaunching] = useState(false);
  const captured = useRef<{ wabaId?: string; phoneNumberId?: string }>({});

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!FB_MESSAGE_ORIGINS.has(event.origin)) return;
      let data: unknown;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      const payload = data as {
        type?: string;
        event?: string;
        data?: { waba_id?: string; phone_number_id?: string };
      };
      if (payload?.type !== 'WA_EMBEDDED_SIGNUP') return;

      if (payload.event === 'FINISH') {
        captured.current = {
          wabaId: payload.data?.waba_id,
          phoneNumberId: payload.data?.phone_number_id,
        };
      } else if (payload.event === 'CANCEL') {
        setLaunching(false);
        onCancelOrError('WhatsApp connection was cancelled.');
      } else if (payload.event === 'ERROR') {
        setLaunching(false);
        onCancelOrError('Meta reported an error during WhatsApp signup.');
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onCancelOrError]);

  async function launch(onResult: (result: EmbeddedSignupResult) => Promise<void>): Promise<void> {
    const appId = import.meta.env.VITE_WHATSAPP_APP_ID;
    const configId = import.meta.env.VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID;
    if (!configId) {
      onCancelOrError(
        'WhatsApp guided connect is not configured yet (missing VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIG_ID). Use "Connect manually" below, or ask an admin to finish setup.',
      );
      return;
    }

    setLaunching(true);
    captured.current = {};
    try {
      const FB = await loadFacebookSdk(appId);
      FB.login(
        (response) => {
          void (async () => {
            const code = response.authResponse?.code;
            if (!code) {
              setLaunching(false);
              onCancelOrError('WhatsApp connection was cancelled.');
              return;
            }
            const { wabaId, phoneNumberId } = captured.current;
            if (!wabaId || !phoneNumberId) {
              setLaunching(false);
              onCancelOrError(
                'Did not receive the WhatsApp Business Account details from Meta. Please try again.',
              );
              return;
            }
            try {
              await onResult({ code, wabaId, phoneNumberId });
            } finally {
              setLaunching(false);
            }
          })();
        },
        {
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
        },
      );
    } catch (err) {
      setLaunching(false);
      onCancelOrError(err instanceof Error ? err.message : 'Could not start WhatsApp connect.');
    }
  }

  return { launching, launch };
}
